import { useState, useMemo, useEffect } from "react";
import SelectMenu from "./SelectMenu";
import Input from "./Input";
import PrimaryButton from "./PrimaryButton";
import SecondaryButton from "./SecondaryButton";
import { formatDuration } from "../../utility/bookingUtils";

const DurationPicker = ({ defaultDuration, onDurationUpdatedHandler }) => {
  const custom = "Custom";
  const defaultMinutes = 60;
  const [duration, setDuration] = useState(
    defaultDuration ? defaultDuration : defaultMinutes
  );
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customDurationValue, setCustomDurationValue] = useState("");
  const [customDurationError, setCustomDurationError] = useState(null);

  const durationData = useMemo(() => {
    // Expanded predefined durations with better coverage
    const predefinedDurations = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480];
    if (
      !predefinedDurations.includes(duration) &&
      duration !== custom &&
      typeof duration === "number"
    ) {
      predefinedDurations.push(duration);
      predefinedDurations.sort((a, b) => a - b);
    }
    const formattedDurations = predefinedDurations.map((duration) => {
      return {
        id: duration,
        name: formatDuration(duration, { compact: true }),
      };
    });
    formattedDurations.push({
      id: custom,
      name: custom,
    });
    return formattedDurations;
  }, [duration]);

  useEffect(() => {
    if (duration !== custom && typeof duration === "number") {
      onDurationUpdatedHandler(duration);
    }
    if (duration === custom) {
      setShowCustomInput(true);
    } else {
      setShowCustomInput(false);
    }
  }, [duration]); // Removed onDurationUpdatedHandler to prevent infinite loop

  // Smart duration parsing function
  const parseSmartDuration = (input) => {
    if (!input || input.trim() === "") return null;

    const trimmed = input.trim().toLowerCase();

    // Match patterns like "1.5h", "90m", "2 hours", "30 minutes"
    const hourPattern = /^(\d+(?:\.\d+)?)\s*(?:h|hour|hours)$/;
    const minutePattern = /^(\d+(?:\.\d+)?)\s*(?:m|min|minute|minutes)$/;

    const hourMatch = trimmed.match(hourPattern);
    const minuteMatch = trimmed.match(minutePattern);

    if (hourMatch) {
      const hours = parseFloat(hourMatch[1]);
      return Math.round(hours * 60);
    } else if (minuteMatch) {
      const minutes = parseFloat(minuteMatch[1]);
      return Math.round(minutes);
    } else if (!isNaN(trimmed)) {
      // Just a number - assume minutes
      return Math.round(parseFloat(trimmed));
    }

    return null;
  };

  const validateCustomDuration = (value) => {
    if (value === "" || value === null || value === undefined) {
      setCustomDurationError("Duration is required");
      return false;
    }

    const durationInMinutes = parseSmartDuration(value);
    if (durationInMinutes === null) {
      setCustomDurationError(
        "Invalid format. Use '30m', '1.5h', '2 hours', etc."
      );
      return false;
    }

    if (durationInMinutes < 5 || durationInMinutes > 1440) {
      setCustomDurationError("Must be between 5 minutes and 24 hours");
      return false;
    }

    setCustomDurationError(null);
    return true;
  };

  const handleCustomDurationChange = (value) => {
    setCustomDurationValue(value);
    if (validateCustomDuration(value)) {
      const durationInMinutes = parseSmartDuration(value);
      onDurationUpdatedHandler(durationInMinutes);
    }
  };

  const handleCustomDone = () => {
    if (validateCustomDuration(customDurationValue)) {
      const durationInMinutes = parseSmartDuration(customDurationValue);
      setDuration(durationInMinutes);
      setShowCustomInput(false);
    }
  };

  const handleCustomCancel = () => {
    setShowCustomInput(false);
    setCustomDurationValue("");
    setCustomDurationError(null);
    setDuration(defaultDuration ? defaultDuration : defaultMinutes);
  };

  return (
    <div className="space-y-4">
      <SelectMenu
        label={"Duration"}
        data={durationData}
        onSelect={(duration) => setDuration(duration.id)}
        defaultSelected={useMemo(
          () => durationData?.find((data) => data.id === duration),
          [durationData, duration]
        )}
      />

      {showCustomInput && (
        <div className="mt-4 p-4 border border-ink-100 rounded-lg bg-cream-100">
          <div className="space-y-4">
            <div>
              <Input
                id="custom-duration"
                name="custom-duration"
                label="Custom duration"
                placeholder="e.g., 1.5h, 90m, 2 hours"
                value={customDurationValue}
                onChange={(event) =>
                  handleCustomDurationChange(event.target.value)
                }
                errormessage={customDurationError}
              />
              <p className="mt-1 text-sm text-ink-500">
                Examples: 30m, 1.5h, 2 hours, 90 minutes
              </p>
            </div>
            <div className="flex gap-x-3 justify-end">
              <SecondaryButton onClick={handleCustomCancel}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={handleCustomDone}
                disabled={!!customDurationError}
              >
                Done
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DurationPicker;
